import { t, fmt } from '@/i18n';
import { formatPrice } from './format';
import type { RegistrationStatusProps } from './types';

export type StatusTone = 'success' | 'warning' | 'info' | 'danger' | 'neutral';
export type StatusIcon = 'check' | 'clock' | 'badge-check' | 'position' | 'users' | 'x' | 'no-symbol';

export interface StatusContent {
  tone: StatusTone;
  icon: StatusIcon;
  /** Short label for the pill badge. */
  badge: string;
  /** The one-sentence answer to "did I get in?". */
  headline: string;
  /** Supporting line under the headline. */
  subtext: string;
}

/**
 * Single source of truth for registration status → badge + human copy, shared by
 * every attendee-facing surface. Covers the 9 states in the design prototype
 * (Screen 3), including the honest waitlist-position line. All copy is translated.
 */
export function getStatusContent({
  status,
  cancellation_reason,
  payment_status,
  price,
  currency,
  waitlist_position,
}: RegistrationStatusProps): StatusContent {
  if (status === 'confirmed') {
    if (payment_status === 'unpaid' && price) {
      return {
        tone: 'warning',
        icon: 'clock',
        badge: t('Approved'),
        headline: t("You're in — payment needed"),
        subtext: fmt('Transfer %(price)s to keep your spot.', { price: formatPrice(price, currency) }),
      };
    }
    if (payment_status === 'submitted') {
      return {
        tone: 'info',
        icon: 'badge-check',
        badge: t('Payment Submitted'),
        headline: t('Payment submitted'),
        subtext: t("The organizer is confirming your payment. You're in once it's verified."),
      };
    }
    if (payment_status === 'verified') {
      return {
        tone: 'success',
        icon: 'check',
        badge: t('Confirmed'),
        headline: t("You're all set!"),
        subtext: t('Registered and paid. See you there.'),
      };
    }
    return {
      tone: 'success',
      icon: 'check',
      badge: t('Confirmed'),
      headline: t("You're in!"),
      subtext: t('Your spot is confirmed. See you there.'),
    };
  }

  if (status === 'waitlisted') {
    if (waitlist_position) {
      return {
        tone: 'info',
        icon: 'position',
        badge: t('Waitlisted'),
        headline: fmt("You're #%(position)s on the waitlist", { position: waitlist_position }),
        subtext: t('As spots open, waitlisted guests are confirmed automatically and emailed right away.'),
      };
    }
    return {
      tone: 'info',
      icon: 'users',
      badge: t('Waitlisted'),
      headline: t("You're on the waitlist"),
      subtext: t('The organizer will notify you if a spot opens.'),
    };
  }

  if (status === 'pending_review') {
    return {
      tone: 'warning',
      icon: 'clock',
      badge: t('Pending Review'),
      headline: t('Awaiting organizer review'),
      subtext: t("You'll get an email as soon as the organizer reviews your registration."),
    };
  }

  if (status === 'cancelled') {
    // A declined pending request is now cancelled + declined (issue #400); keep the honest
    // "wasn't approved" copy for that case rather than the generic cancellation message.
    if (cancellation_reason === 'declined') {
      return {
        tone: 'danger',
        icon: 'x',
        badge: t('Declined'),
        headline: t("Your registration wasn't approved"),
        subtext: t('Contact the organizer if you think this is a mistake.'),
      };
    }
    return {
      tone: 'neutral',
      icon: 'no-symbol',
      badge: t('Cancelled'),
      headline: t('This registration was cancelled'),
      subtext: t('You can register again from the event page if spots remain.'),
    };
  }

  return {
    tone: 'neutral',
    icon: 'no-symbol',
    badge: status,
    headline: '',
    subtext: '',
  };
}
